import { cn } from '@/lib/utils';

export type SentimentSummaryViewModel = {
  positivePercent: number;
  counts: {
    positive: number;
    neutral: number;
    negative: number;
    total: number;
  };
  dominantSentiment: string;
  averageScore: number;
  source?: 'global' | 'personalized';
  fallbackUsed?: boolean;
};

interface SentimentBarProps {
  summary: SentimentSummaryViewModel;
  variant?: 'compact' | 'standard' | 'admin-analytics';
  className?: string;
  label?: string;
  showMeta?: boolean;
}

const SENTIMENT_MIDPOINT = 50;

const formatSentimentLabel = (value?: string) => {
  const safe = String(value || 'mid').trim();
  if (!safe) return 'Mid';
  if (safe === 'notIt') return 'Not It';
  return safe.charAt(0).toUpperCase() + safe.slice(1);
};

const clampSentimentPercent = (value: unknown) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return SENTIMENT_MIDPOINT;
  return Math.max(1, Math.min(100, Math.round(parsed)));
};

export function SentimentBar({
  summary,
  variant = 'standard',
  className,
  label = 'Nosh Sentiment Bar',
  showMeta = true,
}: SentimentBarProps) {
  const percentage = clampSentimentPercent(summary.positivePercent);
  const fillLeft = Math.min(percentage, SENTIMENT_MIDPOINT);
  const fillWidth = Math.abs(percentage - SENTIMENT_MIDPOINT);
  const isRegressed = percentage < SENTIMENT_MIDPOINT;
  const total = summary.counts?.total || 0;
  const metaText = summary.source === 'personalized'
    ? `Personalized sentiment (${total} cohort reviews)`
    : summary.fallbackUsed
      ? `Global sentiment fallback (${total} reviews)`
      : `Global sentiment (${total} reviews)`;

  return (
    <div className={cn('space-y-2', className)}>
      {variant !== 'compact' && (
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-slate-800">{label}</p>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
            {formatSentimentLabel(summary.dominantSentiment)}
          </span>
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="relative h-1.5 flex-1 overflow-visible rounded-full bg-slate-200">
          <div
            className={cn(
              'absolute top-0 h-full rounded-full',
              isRegressed
                ? 'bg-gradient-to-r from-[#FF0E00] to-[#B88A00]'
                : 'bg-gradient-to-r from-[#B88A00] to-[#105D38]'
            )}
            style={{ left: `${fillLeft}%`, width: `${fillWidth}%` }}
          />
          <div
            aria-hidden="true"
            className="absolute left-1/2 top-1/2 h-3 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-slate-900/70"
          />
        </div>
        <span className={cn('font-semibold text-slate-900', variant === 'compact' ? 'text-xs' : 'text-sm')}>
          {percentage}%
        </span>
      </div>

      {(showMeta || variant === 'admin-analytics') && (
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
          <span>{metaText}</span>
          {variant === 'admin-analytics' && (
            <>
              <span>Positive {summary.counts.positive}</span>
              <span>Neutral {summary.counts.neutral}</span>
              <span>Negative {summary.counts.negative}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
